import {useEffect, useRef} from 'react';
import z from 'zod';
import mixpanel, {Config, OverridedMixpanel} from 'mixpanel-browser';

type CaptureException = (err: Error) => string | undefined;

export type UseMixpanelProps = {
  isProduction: boolean;
  channelCode: string;
  getMixpanelFlagValues: () => ItemSchema;
  getExperimentValue: (experimentName: string) => string;
  getTrackingDeviceId: () => Promise<string>;
  captureException?: CaptureException;
};

const itemSchema = z.array(
  z.object({
    experimentName: z.optional(z.string()),
    variants: z.optional(z.array(z.string())),
    channels: z.array(z.string()),
    paths: z.array(z.string()),
    mixpanelOptions: z.optional(z.custom<Config>()),
  })
);

export type ItemSchema = z.infer<typeof itemSchema>;

export const useMixpanel = ({
  isProduction,
  channelCode,
  getMixpanelFlagValues,
  getExperimentValue,
  getTrackingDeviceId,
  captureException,
}: UseMixpanelProps) => {
  const devToken = 'c1678f7808bb03e24ff367351179d497';
  const prodToken = '0e168640e694d9f2c1867026675a66ea';
  const mixpanelFlagValues = getMixpanelFlagValues();
  const mixpanelInstance = useRef<OverridedMixpanel | null>(null);

  useEffect(() => {
    if (mixpanelInstance.current !== null) {
      return;
    }

    let shouldInitialiseMixpanel = false;
    let mixpanelOptions: Partial<Config> = {};
    const token = isProduction ? prodToken : devToken;
    const result = itemSchema.safeParse(mixpanelFlagValues);
    const mixpanelDebug = localStorage.getItem('mixpanel_debug') === 'true';

    if (!result.success) {
      captureException?.(result.error);
      return;
    }

    if (result.success) {
      const {initialiseMixpanel, options} = shouldInitialiseRecordingMixpanel({
        data: result.data,
        channelCode,
        getExperimentValue,
      });
      shouldInitialiseMixpanel = initialiseMixpanel;
      mixpanelOptions = options;
    }

    if (!shouldInitialiseMixpanel) {
      return;
    }

    const initialiseMixpanel = async () => {
      const deviceId = await getTrackingDeviceId();

      try {
        mixpanel.init(token, {
          debug: mixpanelDebug,
          record_sessions_percent: 0,
          loaded: (mixpanelInstance) => {
            const mParticle_device_id = deviceId;
            mixpanelInstance.register({
              $device_id: mParticle_device_id,
              distinct_id: '$device:' + mParticle_device_id,
            });
          },
          ...mixpanelOptions,
        });
      } catch (error) {
        captureException?.(error as Error);
      }

      const recordingProperties = mixpanel.get_session_recording_properties();

      if (!('$mp_replay_id' in recordingProperties)) {
        mixpanel.start_session_recording();
      }

      mixpanelInstance.current = mixpanel;
    };

    initialiseMixpanel();
  }, [
    captureException,
    mixpanelFlagValues,
    mixpanelInstance,
    isProduction,
    getTrackingDeviceId,
    channelCode,
    getExperimentValue,
  ]);

  return {mixpanelInstance};
};

export const shouldInitialiseRecordingMixpanel = ({
  data,
  channelCode,
  getExperimentValue,
}: {
  data: ItemSchema;
  channelCode: string;
  getExperimentValue: UseMixpanelProps['getExperimentValue'];
}) => {
  let initialiseMixpanel = false;
  let options: Partial<Config> = {};
  const HOMEPAGE_FLAG = '__HOMEPAGE__';
  const isHomepage = () => {
    const {pathname, search, hash} = window.location;
    return pathname === '/' && search === '' && hash === '';
  };

  if (data.length === 0) {
    return {
      initialiseMixpanel,
      options,
    };
  }

  for (const item of data) {
    for (const path of item.paths) {
      const urlMatchesPath =
        path === HOMEPAGE_FLAG ? isHomepage() : location.href.includes(path);

      const shouldCheckExperiment =
        item.experimentName &&
        item.variants &&
        urlMatchesPath &&
        item.channels.includes(channelCode);

      if (shouldCheckExperiment) {
        const experimentValue = getExperimentValue(
          item.experimentName as string
        );
        const isUserInsideVariant = item.variants?.some(
          (variant) => variant === experimentValue
        );
        if (isUserInsideVariant) {
          initialiseMixpanel = true;
          options = item.mixpanelOptions ? item.mixpanelOptions : {};
          break;
        }
      }

      const shouldRecordPage =
        !item.experimentName &&
        urlMatchesPath &&
        item.channels.includes(channelCode);
      if (shouldRecordPage) {
        initialiseMixpanel = true;
        options = item.mixpanelOptions ? item.mixpanelOptions : {};
        break;
      }
    }
  }

  return {initialiseMixpanel, options};
};
